/**
 * Assistant message list (frontend-display.md §4.2 / frontend-ux-design.md §4.3).
 *
 * Renders `message.parts[]`:
 *   - `text`        → bubbles (user = right-aligned accent; assistant = bg-muted,
 *                      Markdown via `MarkdownEditor` read-only)
 *   - `tool-call`   → the E2 Approve/Reject card
 *   - `tool-result` → a deep-linking E1 chip
 *
 * E0 surface: parts float on the rail's paper; the only border is the tool card.
 */
import { cn } from "@/lib/utils";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";
import type {
  AssistantMessage,
  AssistantToolCallPart,
} from "@/components/assistant/types";
import { ToolApprovalCard } from "@/components/assistant/ToolApprovalCard";
import { ToolResultChip } from "@/components/assistant/ToolResultChip";

interface MessageListProps {
  messages: AssistantMessage[];
  onApprove: (toolCallId: string) => void;
  onReject: (toolCallId: string) => void;
  /** True while a (sample) assistant turn is "streaming" — shows the indicator. */
  isStreaming?: boolean;
}

export function MessageList({
  messages,
  onApprove,
  onReject,
  isStreaming,
}: MessageListProps) {
  return (
    <div
      className="flex flex-col gap-3"
      role="log"
      aria-label="Assistant conversation"
      aria-live="polite"
    >
      {messages.map((message) => (
        <MessageRow
          key={message.id}
          message={message}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
      {isStreaming && <TypingIndicator />}
    </div>
  );
}

function MessageRow({
  message,
  onApprove,
  onReject,
}: {
  message: AssistantMessage;
  onApprove: (toolCallId: string) => void;
  onReject: (toolCallId: string) => void;
}) {
  const isUser = message.role === "user";

  return (
    <div
      data-testid="assistant-message"
      data-role={message.role}
      className={cn("flex flex-col gap-2", isUser ? "items-end" : "items-start")}
    >
      {message.parts.map((part, i) => {
        const key = `${message.id}-${i}`;
        if (part.type === "text") {
          const isError = part.tone === "error";
          return (
            <div
              key={key}
              data-testid={isError ? "assistant-error-notice" : undefined}
              className={cn(
                "max-w-[85%] rounded-2xl px-3 py-2 text-sm",
                isError
                  ? "border border-border bg-card text-muted-foreground"
                  : isUser
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground",
              )}
            >
              {isUser ? (
                <p className="whitespace-pre-wrap break-words">{part.text}</p>
              ) : isError ? (
                <p className="whitespace-pre-wrap break-words">{part.text}</p>
              ) : (
                <MarkdownEditor value={part.text} readOnly />
              )}
            </div>
          );
        }
        if (part.type === "tool-call") {
          return (
            <div key={key} className="w-full max-w-[95%]">
              <ToolApprovalCard
                part={part as AssistantToolCallPart}
                onApprove={onApprove}
                onReject={onReject}
              />
            </div>
          );
        }
        // tool-result
        return <ToolResultChip key={key} part={part} />;
      })}
    </div>
  );
}

/** 3-dot typing indicator (frontend-ux-design.md §8). */
function TypingIndicator() {
  return (
    <div
      data-testid="assistant-typing"
      className="flex items-center gap-1 rounded-2xl bg-muted px-3 py-2.5"
      aria-label="Assistant is typing"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-status-pulse rounded-full bg-muted-foreground"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </div>
  );
}

export default MessageList;
